import { Building2, CheckCircle2, ChevronRight, Clock3, Inbox } from 'lucide-react'
import { GestorEntradasFilters } from '@/components/Gestores/GestorEntradasFilters'
import { GESTOR_SETOR_LABELS } from '@/config/gestorAccess'
import type { EntradaStatus } from '@/components/Gestores/gestorClienteEntradaConfig'
import { ENTRADA_STEP_LABELS, ENTRADA_STEP_KEYS } from '@/components/Gestores/gestorClienteEntradaConfig'
import { toUiDealStage } from '@/adapters/deals'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorListTableShell,
  GestorTableAvatar,
} from '@/components/Gestores/GestorListTableShell'
import type { GestorEntradaRow } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type Props = {
  entradas: GestorEntradaRow[]
  loading: boolean
  error: string | null
  search: string
  onSearchChange: (v: string) => void
  statusFilter: EntradaStatus
  setStatusFilter: (v: EntradaStatus) => void
  onSelect: (id: string) => void
}

function nextPendingStepLabel(entrada: GestorEntradaRow) {
  const pending = ENTRADA_STEP_KEYS.find((key) => !entrada.steps[key]?.done)
  return pending ? ENTRADA_STEP_LABELS[pending] : 'Fluxo concluído'
}

function handoverSlaLabel(dueAt: string | null, dealEtapa: string) {
  if (!dueAt || toUiDealStage(dealEtapa) !== 'handover') return null
  const due = new Date(dueAt)
  const overdue = due.getTime() < Date.now()
  const hours = Math.max(1, Math.round(Math.abs(due.getTime() - Date.now()) / (60 * 60 * 1000)))
  return overdue ? `Atrasado ${hours}h` : `Em ${hours}h`
}

export function GestorEntradasTab({
  entradas,
  loading,
  error,
  search,
  onSearchChange,
  statusFilter,
  setStatusFilter,
  onSelect,
}: Props) {
  return (
    <div className='space-y-3'>
      <header>
        <h2 className='text-xl font-semibold text-text-primary'>Entradas de clientes</h2>
        <p className='mt-2 max-w-3xl text-sm leading-relaxed text-text-muted'>
          Novos clientes com contrato assinado entram aqui por setor. Você vê apenas entradas do seu
          time conforme os serviços contratados (ex.: Suporte e Marketing).
        </p>
      </header>

      <GestorEntradasFilters
        search={search}
        onSearchChange={onSearchChange}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
      />

      {error && (
        <div className='rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger'>
          {error}
        </div>
      )}

      <GestorListTableShell
        loading={loading}
        loadingMessage='Carregando entradas…'
        empty={!loading && entradas.length === 0}
        emptyIcon={
          <Inbox size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
        }
        emptyTitle={
          statusFilter === 'CONCLUIDO'
            ? 'Nenhuma entrada concluída'
            : 'Nenhuma entrada em andamento'
        }
        emptyHint='Quando um contrato for assinado com serviços do seu setor, o cliente aparece aqui.'
      >
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Cliente', 'Deal', 'Setor', 'Progresso', 'Próxima etapa', 'SLA', ''].map((h) => (
                <th
                  key={h || 'actions'}
                  className={GESTOR_TABLE_TH_CLASS}
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entradas.map((entrada) => {
              const sla = handoverSlaLabel(entrada.slaHandoverDueAt, entrada.deal.etapa)
              const progressPct = Math.round(
                (entrada.progress.done / entrada.progress.total) * 100,
              )
              return (
                <tr
                  key={entrada.id}
                  className={GESTOR_TABLE_TR_CLASS}
                  onClick={() => onSelect(entrada.id)}
                >
                  <td className='px-4 py-3'>
                    <div className='flex items-center gap-3'>
                      <GestorTableAvatar name={entrada.empresa.nome} />
                      <div className='min-w-0'>
                        <p
                          className='font-medium text-[var(--font-color-body)]'
                          style={{ fontSize: 'var(--font-size-sm)' }}
                        >
                          {entrada.empresa.nome}
                        </p>
                        {entrada.servicosLabel && (
                          <p className='mt-0.5 truncate text-[11px] text-[var(--font-color-muted)]'>
                            {entrada.servicosLabel}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className='px-4 py-3'>
                    <span
                      className='text-[var(--font-color-muted)]'
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {entrada.deal.nome}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='inline-flex items-center gap-1 text-[var(--font-color-muted)]' style={{ fontSize: 'var(--font-size-sm)' }}>
                      <Building2 size={12} aria-hidden />
                      {GESTOR_SETOR_LABELS[entrada.setor] ?? entrada.setor}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    <div className='min-w-[100px]'>
                      <div className='mb-1 flex items-center justify-between text-[10px] text-[var(--font-color-muted)]'>
                        <span>
                          {entrada.progress.done}/{entrada.progress.total}
                        </span>
                        <span>{progressPct}%</span>
                      </div>
                      <div className='h-1.5 overflow-hidden rounded-full bg-white/[0.06]'>
                        <div
                          className='h-full rounded-full bg-accent transition-all'
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className='px-4 py-3'>
                    <span
                      className='text-[var(--font-color-muted)]'
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {nextPendingStepLabel(entrada)}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    {entrada.status === 'CONCLUIDO' ? (
                      <span className='inline-flex items-center gap-1 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-400'>
                        <CheckCircle2 size={10} /> Concluído
                      </span>
                    ) : sla ? (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[9px] font-bold uppercase',
                          sla.includes('Atrasado')
                            ? 'border-red-500/20 bg-red-500/10 text-red-400'
                            : 'border-amber-500/20 bg-amber-500/10 text-amber-400',
                        )}
                      >
                        <Clock3 size={10} /> Handover {sla}
                      </span>
                    ) : (
                      <span
                        className='text-[var(--font-color-muted)]'
                        style={{ fontSize: 'var(--font-size-sm)' }}
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className='px-4 py-3'>
                    <ChevronRight size={14} className='text-[var(--text-muted)]' aria-hidden />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </GestorListTableShell>
    </div>
  )
}
